import React, { useState, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';
import { CheckCircle, ChevronRight, ChevronLeft, AlertTriangle, TrendingUp, Shield, Zap, Download, User, Mail, Building, Briefcase } from 'lucide-react';
import './AssessmentPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AssessmentPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [leadData, setLeadData] = useState({
    firstName: '',
    email: '',
    companyName: '',
    role: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const resultsRef = useRef(null);

  const dimensions = [
    {
      name: 'People',
      description: 'Leadership fit, capability, and readiness',
      questions: [
        {
          id: 1,
          text: 'People in leadership roles have the capability needed to lead both people and performance effectively.'
        },
        {
          id: 2,
          text: 'The organization has sufficient leadership depth and is not overly dependent on one or two individuals.'
        }
      ]
    },
    {
      name: 'Roles',
      description: 'Role clarity, decision rights, and accountability',
      questions: [
        {
          id: 3,
          text: 'People have a clear understanding of their roles and what they are accountable for.'
        },
        {
          id: 4,
          text: 'It is clear who owns which decisions and decisions are made at the appropriate level.'
        }
      ]
    },
    {
      name: 'Skills',
      description: 'Capability alignment, skill depth, future readiness',
      questions: [
        {
          id: 5,
          text: 'The organization currently has the skills needed to deliver its strategy.'
        },
        {
          id: 6,
          text: 'The organization is building the capabilities needed for the next stage of growth.'
        }
      ]
    },
    {
      name: 'Environment',
      description: 'Culture, ways of working, performance enablement',
      questions: [
        {
          id: 7,
          text: 'People feel safe to speak up, challenge constructively, and have their input taken seriously.'
        },
        {
          id: 8,
          text: 'Collaboration and ways of working across teams enable effective execution.'
        }
      ]
    },
    {
      name: 'Incentives',
      description: 'Behavioral alignment and consequence management',
      questions: [
        {
          id: 9,
          text: 'What gets rewarded in this organization aligns with the behaviors leadership says it values.'
        },
        {
          id: 10,
          text: 'Performance is recognized and addressed in a way that is fair and consistent.'
        }
      ]
    },
    {
      name: 'Time',
      description: 'Strategic pacing, capacity load, readiness',
      questions: [
        {
          id: 11,
          text: 'Priorities and workload are aligned with available capacity.'
        },
        {
          id: 12,
          text: 'The organization can absorb change without destabilizing performance.'
        }
      ]
    }
  ];

  const ratingScale = [
    { value: 1, label: 'Not true at all' },
    { value: 2, label: 'Rarely true' },
    { value: 3, label: 'Sometimes true' },
    { value: 4, label: 'Mostly true' },
    { value: 5, label: 'Always true' }
  ];

  // Flatten questions for navigation
  const allQuestions = dimensions.flatMap(dim => 
    dim.questions.map(q => ({ ...q, dimension: dim.name, dimensionDesc: dim.description }))
  );

  const handleAnswer = (value) => {
    setAnswers({ ...answers, [currentStep]: value });
  };

  const handleNext = () => {
    if (currentStep < allQuestions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Show lead capture form instead of results
      setShowLeadCapture(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleLeadInputChange = (e) => {
    const { name, value } = e.target;
    setLeadData(prev => ({ ...prev, [name]: value }));
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    const dimensionScores = calculateDimensionScores();
    const overallScore = calculateOverallScore();
    const signal = getOverallSignal(overallScore);

    try {
      const response = await fetch(`${API_URL}/api/assessment-lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: leadData.firstName,
          email: leadData.email,
          company_name: leadData.companyName,
          role: leadData.role,
          overall_score: overallScore,
          dimension_scores: dimensionScores,
          signal_title: signal.title
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      // Show results after successful submission
      setShowLeadCapture(false);
      setShowResults(true);
    } catch (error) {
      console.error('Error submitting lead:', error);
      // Still show results even if API fails
      setShowLeadCapture(false);
      setShowResults(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = () => {
    // Create a printable version and trigger print dialog
    window.print();
  };

  // Calculate dimension scores
  const calculateDimensionScores = () => {
    const scores = {};
    dimensions.forEach((dim, dimIndex) => {
      const q1Index = dimIndex * 2;
      const q2Index = dimIndex * 2 + 1;
      const q1Score = answers[q1Index] || 0;
      const q2Score = answers[q2Index] || 0;
      scores[dim.name] = (q1Score + q2Score) / 2;
    });
    return scores;
  };

  // Calculate overall score
  const calculateOverallScore = () => {
    const dimensionScores = calculateDimensionScores();
    const total = Object.values(dimensionScores).reduce((sum, val) => sum + val, 0);
    return total / 6;
  };

  const getOverallSignal = (score) => {
    if (score >= 4.11) {
      return {
        title: 'Strong Enabler',
        icon: <Shield size={48} />,
        color: '#22c55e',
        description: 'Core elements of your organization appear aligned and supporting execution. Your organizational system shows strong enablement across dimensions.',
        suggestions: [
          'Continue reinforcing leadership development practices',
          'Monitor for emerging constraints as the organization grows',
          'Consider sharing best practices across teams'
        ]
      };
    } else if (score >= 3.41) {
      return {
        title: 'Functional but Fragile',
        icon: <TrendingUp size={48} />,
        color: '#eab308',
        description: 'The organization is functioning reasonably well, but inconsistencies may create hidden strain. Some dimensions may be compensating for weaknesses in others.',
        suggestions: [
          'Identify which dimensions are carrying extra load',
          'Address role clarity and decision-making gaps',
          'Build leadership depth to reduce single-point dependencies'
        ]
      };
    } else if (score >= 2.80) {
      return {
        title: 'Constraint Zone',
        icon: <AlertTriangle size={48} />,
        color: '#f97316',
        description: 'One or more structural conditions may be slowing execution. Execution challenges may be systemic rather than isolated.',
        suggestions: [
          'Prioritize leadership capability and capacity issues',
          'Clarify accountability and decision rights',
          'Realign incentives with desired behaviors',
          'Consider capacity constraints before adding new initiatives'
        ]
      };
    } else {
      return {
        title: 'System Under Strain',
        icon: <Zap size={48} />,
        color: '#ef4444',
        description: 'Multiple system conditions may be limiting organizational effectiveness. Performance issues are likely widespread and interconnected.',
        suggestions: [
          'Immediate focus on stabilizing core leadership',
          'Reduce change load until capacity improves',
          'Address fundamental role and accountability gaps',
          'Consider external support for diagnostic and intervention'
        ]
      };
    }
  };

  const progress = ((currentStep + 1) / allQuestions.length) * 100;

  // Lead Capture Form
  if (showLeadCapture) {
    return (
      <div className="assessment-page">
        <Header />
        <section className="lead-capture-section">
          <div className="assessment-container">
            <div className="lead-capture-card">
              <div className="lead-capture-icon">
                <CheckCircle size={64} color="#22c55e" />
              </div>
              <h1 className="lead-capture-title">Assessment Complete!</h1>
              <p className="lead-capture-subtitle">
                Enter your details below to view your personalized results and receive a downloadable copy.
              </p>
              
              <form onSubmit={handleLeadSubmit} className="lead-capture-form">
                <div className="lead-input-group">
                  <label htmlFor="firstName">
                    <User size={18} />
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={leadData.firstName}
                    onChange={handleLeadInputChange}
                    placeholder="Enter your first name"
                    required
                    data-testid="lead-firstname"
                  />
                </div>
                
                <div className="lead-input-group">
                  <label htmlFor="email">
                    <Mail size={18} />
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={leadData.email}
                    onChange={handleLeadInputChange}
                    placeholder="Enter your email"
                    required
                    data-testid="lead-email"
                  />
                </div>
                
                <div className="lead-input-group">
                  <label htmlFor="companyName">
                    <Building size={18} />
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={leadData.companyName}
                    onChange={handleLeadInputChange}
                    placeholder="Enter your company name"
                    required
                    data-testid="lead-company"
                  />
                </div>
                
                <div className="lead-input-group">
                  <label htmlFor="role">
                    <Briefcase size={18} />
                    Role / Title
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={leadData.role}
                    onChange={handleLeadInputChange}
                    required
                    data-testid="lead-role"
                  >
                    <option value="">Select your role</option>
                    <option value="CEO/Owner">CEO / Owner</option>
                    <option value="HR Leader">HR Leader</option>
                    <option value="Operations">Operations</option>
                    <option value="Executive">Executive</option>
                    <option value="Manager">Manager</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                {submitError && (
                  <div className="lead-error">{submitError}</div>
                )}
                
                <button 
                  type="submit" 
                  className="btn btn-primary lead-submit-btn"
                  disabled={isSubmitting}
                  data-testid="lead-submit"
                >
                  {isSubmitting ? 'Processing...' : 'View My Results'}
                  <ChevronRight size={20} />
                </button>
              </form>
              
              <p className="lead-privacy-note">
                Your information is secure and will only be used to send you assessment-related communications.
              </p>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (showResults) {
    const overallScore = calculateOverallScore();
    const dimensionScores = calculateDimensionScores();
    const signal = getOverallSignal(overallScore);

    // Find lowest scoring dimensions
    const sortedDimensions = Object.entries(dimensionScores)
      .sort((a, b) => a[1] - b[1])
      .slice(0, 2);

    return (
      <div className="assessment-page">
        <Header />
        <section className="assessment-results">
          <div className="assessment-container">
            <div className="results-card">
              <div className="results-header">
                <div className="results-icon" style={{ color: signal.color }}>
                  {signal.icon}
                </div>
                <h1 className="results-title">Your Organizational Effectiveness Snapshot</h1>
                <p className="results-intro">
                  This assessment measured six dimensions that strongly influence how organizations perform.
                </p>
              </div>

              <div className="results-score-section">
                <div className="overall-score">
                  <div className="score-circle" style={{ borderColor: signal.color }}>
                    <div className="score-number" style={{ color: signal.color }}>{overallScore.toFixed(1)}</div>
                    <div className="score-label">out of 5.0</div>
                  </div>
                  <div className="score-signal">
                    <h2 className="signal-title" style={{ color: signal.color }}>{signal.title}</h2>
                    <p className="signal-description">{signal.description}</p>
                  </div>
                </div>
              </div>

              <div className="dimension-scores">
                <h3 className="dimension-scores-title">Dimension Breakdown</h3>
                <div className="dimension-grid">
                  {dimensions.map((dim) => {
                    const score = dimensionScores[dim.name];
                    const scoreColor = score >= 4 ? '#22c55e' : score >= 3 ? '#eab308' : score >= 2 ? '#f97316' : '#ef4444';
                    return (
                      <div key={dim.name} className="dimension-item">
                        <div className="dimension-header">
                          <span className="dimension-name">{dim.name}</span>
                          <span className="dimension-score" style={{ color: scoreColor }}>{score.toFixed(1)}</span>
                        </div>
                        <div className="dimension-bar">
                          <div 
                            className="dimension-bar-fill" 
                            style={{ width: `${(score / 5) * 100}%`, backgroundColor: scoreColor }}
                          ></div>
                        </div>
                        <span className="dimension-desc">{dim.description}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="results-suggestions">
                <h3>What This Snapshot Suggests</h3>
                {sortedDimensions.length > 0 && sortedDimensions[0][1] < 3.5 && (
                  <div className="focus-areas">
                    <p className="focus-intro">Your lowest scoring areas that may warrant attention:</p>
                    <ul className="focus-list">
                      {sortedDimensions.map(([name, score]) => (
                        <li key={name}>
                          <strong>{name}</strong> ({score.toFixed(1)}) - {dimensions.find(d => d.name === name)?.description}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <ul className="suggestions-list">
                  {signal.suggestions.map((suggestion, index) => (
                    <li key={index}>{suggestion}</li>
                  ))}
                </ul>
              </div>

              <div className="results-disclaimer">
                <h4>What This Check Does Not Show</h4>
                <p>
                  This short diagnostic provides signals, not root causes. It indicates where to look, 
                  not what specifically to fix. A full organizational diagnostic provides deeper analysis 
                  of interdependencies and targeted recommendations.
                </p>
              </div>

              <div className="results-actions" data-testid="results-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={() => window.open('https://form.jotform.com/260186646305054', '_blank')}
                  data-testid="contact-r6-button"
                >
                  Contact R6 Collective™
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={handleDownloadPDF}
                  data-testid="download-button"
                >
                  <Download size={18} />
                  Download Results
                </button>
                <button 
                  className="btn btn-tertiary" 
                  onClick={() => { setShowResults(false); setShowLeadCapture(false); setCurrentStep(0); setAnswers({}); setLeadData({ firstName: '', email: '', companyName: '', role: '' }); }}
                  data-testid="retake-button"
                >
                  Retake Assessment
                </button>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const currentQuestion = allQuestions[currentStep];

  return (
    <div className="assessment-page" data-testid="assessment-page">
      <PageSEO
        title="Organizational Assessment"
        path="/assessment"
        description="Free 2-minute organizational effectiveness diagnostic. Assess your talent management across 6 critical dimensions and receive a customized improvement roadmap."
      />
      <Header />
      
      <section className="assessment-hero" data-testid="assessment-hero">
        <div className="assessment-container">
          <div className="assessment-badge" data-testid="assessment-badge">Free Diagnostic</div>
          <h1 className="assessment-title">Organizational Effectiveness Check</h1>
          <p className="assessment-subtitle">
            Short Diagnostic – 12 Questions · ~2 minutes
          </p>
          <p className="assessment-instruction">
            Respond based on how things <strong>actually work</strong> today, not how they are intended to work.
          </p>
          <div className="progress-bar" data-testid="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="progress-text" data-testid="progress-text">
            Question {currentStep + 1} of {allQuestions.length}
          </div>
        </div>
      </section>

      <section className="assessment-content" data-testid="assessment-content">
        <div className="assessment-container">
          <div className="question-card" data-testid="question-card">
            <div className="question-category" data-testid="question-category">{currentQuestion.dimension}</div>
            <div className="question-subtitle">{currentQuestion.dimensionDesc}</div>
            <h2 className="question-text" data-testid="question-text">{currentQuestion.text}</h2>
            <div className="rating-scale" data-testid="rating-scale">
              {ratingScale.map((rating) => (
                <div
                  key={rating.value}
                  className={`rating-item ${answers[currentStep] === rating.value ? 'selected' : ''}`}
                  onClick={() => handleAnswer(rating.value)}
                  data-testid={`rating-${rating.value}`}
                >
                  <div className="rating-number">{rating.value}</div>
                  <div className="rating-label">{rating.label}</div>
                </div>
              ))}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn btn-secondary"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                data-testid="previous-button"
              >
                <ChevronLeft size={20} />
                Previous
              </button>
              <button
                className="btn btn-primary"
                onClick={handleNext}
                disabled={!answers[currentStep]}
                data-testid="next-button"
              >
                {currentStep === allQuestions.length - 1 ? 'View Results' : 'Next'}
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AssessmentPage;
